/** SEND QUOTE — user chooses Email / WhatsApp / both. Never automatic. Uses the STX-Q number only. */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Mail, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { postWithPresend } from "@/components/quotes/QuoteExperiencePanel";

export default function SendQuoteWhatsApp({ quote }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(quote.customer_contact_phone || "");
  const [email, setEmail] = useState(quote.customer_email || quote.contact_email || "");
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const hasStx = (quote.code || "").startsWith("STX-Q-");
  const send = async (mode) => {
    setBusy(true);
    const out = [];
    try {
      if (mode !== "email") {
        if (!to) throw new Error("Enter the recipient mobile with country code");
        const { data } = await postWithPresend(`/vtiger/quotations/${quote.id}/send-whatsapp`, { to, attach_quote_pdf: attach }, user?.role);
        out.push(`WhatsApp SENT to ${data.to}`);
      }
      if (mode !== "whatsapp") {
        if (!email) throw new Error("Enter the recipient email");
        const { data } = await postWithPresend(`/vtiger/quotations/${quote.id}/cover-email`, { to: email, mode: "SEND", attach_quote_pdf: true }, user?.role);
        out.push(data.status === "SENT" || data.status === "ALREADY_SENT" ? "Email sent" : "Outlook draft created");
      }
      toast.success(out.join(" · ")); setOpen(false);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : e.message || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="send-quote-panel">
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!hasStx} title={hasStx ? "" : "Quote needs an STX-Q number first"} data-testid="send-quote-open">
          <Send className="h-4 w-4 mr-1.5" /> Send quote · Email / WhatsApp
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="text-[12px] font-medium text-slate-700">Send {quote.code} — choose channel (nothing is sent automatically)</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="WhatsApp mobile e.g. +919840068999" data-testid="send-quote-mobile" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email" data-testid="send-quote-email" />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600"><input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="send-quote-attach" /> Attach quote PDF (secure, time-limited link on WhatsApp)</label>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => send("email")} disabled={busy} data-testid="send-quote-by-email"><Mail className="h-3.5 w-3.5 mr-1" /> Send by Email</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => send("whatsapp")} disabled={busy} data-testid="send-quote-by-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1" /> Send by WhatsApp</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => send("both")} disabled={busy} data-testid="send-quote-by-both">Send by Email + WhatsApp</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
          {quote.whatsapp_status && <div className="text-[11px] text-slate-500" data-testid="send-quote-last">Last WhatsApp: {quote.whatsapp_status}{quote.whatsapp_sent_at ? ` · ${new Date(quote.whatsapp_sent_at).toLocaleString("en-IN")}` : ""}</div>}
        </div>
      )}
    </div>
  );
}
